import { ActionButton, Icon, Image, Text, InputFieldV2, BadgeV2 } from "../custom-components";
import "../styles/pageConfiguration.css";
import "../styles/templates.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import { IcSuccessColored, IcError, IcWarningColored, IcSuccess, IcTrash, IcNotification, IcSort, IcDownload, IcFilter, IcClose, IcRefresh, IcProfile } from "../custom-components/Icon";
import { useDispatch, useSelector } from "react-redux";
import { exportToCSV } from "../utils/csvExport";
import { generateTransactionId } from "../utils/transactionId";
import { formatToIST } from "../utils/dateUtils";
import config from "../utils/config";

const DataDeletionPurgeDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dashboard = new URL("../assets/dashboard.svg", import.meta.url).href;

  const tenantId = useSelector((state) => state.common.tenant_id);
  const businessId = useSelector((state) => state.common.business_id);
  const sessionToken = useSelector((state) => state.common.session_token);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    dfStatus: [],
    overallStatus: [],
    reason: [],
    dateFrom: "",
    dateTo: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    deletionRequests: 0,
    completed: 0,
    inProgress: 0,
    deferred: 0,
    userNotified: 0,
    notificationPending: 0,
  });

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Helper function to safely convert value to number, defaulting to 0 (handles 0 explicitly)
  const safeNumber = (value) => {
    if (value === undefined || value === null) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Fetch deletion requests from API
  const fetchDeletionRequests = useCallback(async (signal) => {
    if (!tenantId || !businessId || !sessionToken) {
      console.warn("Missing tenantId, businessId, or sessionToken, skipping API call");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const txnId = generateTransactionId();

      const authToken = sessionToken?.startsWith('Bearer ') ? sessionToken : `Bearer ${sessionToken || ''}`;

      const response = await fetch(config.consent_deletion_list, {
        method: 'GET',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-Business-Id': businessId,
          'X-Transaction-Id': txnId,
          'x-session-token': authToken,
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();

      if (signal?.aborted) return;

      const overview = responseData?.data?.overview || {};
      setStats({
        deletionRequests: safeNumber(overview.deletionRequests),
        completed: safeNumber(overview.completed),
        inProgress: safeNumber(overview.inProgress),
        deferred: safeNumber(overview.deferred),
        userNotified: safeNumber(overview.userNotified),
        notificationPending: safeNumber(overview.notificationPending),
      });

      if (responseData?.data?.deletionRequests?.data && Array.isArray(responseData.data.deletionRequests.data)) {
        const apiRequests = responseData.data.deletionRequests.data;

        const mappedRequests = apiRequests.map((item, index) => {
          const processorsMatch = String(item.processors || "").match(/(\d+)\s*\/\s*(\d+)/);
          const processorsCompleted =
            processorsMatch
              ? parseInt(processorsMatch[1], 10)
              : safeNumber(
                item.processorsCompleted ??
                item.processorCompleted ??
                item.completedProcessors ??
                item.deletedProcessors
              );
          const processorsTotal =
            processorsMatch
              ? parseInt(processorsMatch[2], 10)
              : safeNumber(
                item.processorsTotal ??
                item.processorTotal ??
                item.totalProcessors ??
                item.processorCount
              );
          const processorsDisplay = `${processorsCompleted}/${processorsTotal}`;

          const reasonMap = {
            'CONSENT_WITHDRAWN': 'Withdraw',
            'CONSENT_EXPIRED': 'Expiry',
            'EXPIRY': 'Expiry',
            'WITHDRAW': 'Withdraw',
          };
          const reason = reasonMap[item.trigger] || item.trigger || 'Withdraw';

          const dfStatus = item.dfStatus
            ? item.dfStatus.charAt(0).toUpperCase() + item.dfStatus.slice(1).toLowerCase()
            : 'Pending';

          const overallMap = {
            'Done': 'All deleted',
            'DONE': 'All deleted',
            'Partial': 'Partial deleted',
            'PARTIAL': 'Partial deleted',
            'Deferred': 'Deferred',
            'DEFERRED': 'Deferred',
          };
          const overallStatus = overallMap[item.overallCompletion] || item.overallCompletion || 'Pending';

          return {
            id: index + 1,
            consentId: item.consentId || '',
            dataPrincipal: item.dataPrincipal || '',
            reason: reason,
            dfStatus: dfStatus,
            processorsCompleted: processorsCompleted,
            processorsTotal: processorsTotal,
            processorsDisplay: processorsDisplay,
            overallStatus: overallStatus,
            eventTimestamp: item.eventTimestamp,
            eventId: item.eventId,
          };
        });

        setDeletionRequests(mappedRequests);
      } else {
        setDeletionRequests([]);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error("Error fetching deletion requests:", err);
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [tenantId, businessId, sessionToken]);

  // Re-fetch when business group or credentials change
  useEffect(() => {
    if (!tenantId || !businessId || !sessionToken) return;
    const controller = new AbortController();
    fetchDeletionRequests(controller.signal);
    return () => controller.abort();
  }, [fetchDeletionRequests]);

  // Deletion requests from API
  const [deletionRequests, setDeletionRequests] = useState([]);

  // Filter deletion requests based on search query and filters
  const filteredRequests = deletionRequests.filter((item) => {
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        item.consentId?.toLowerCase().includes(query) ||
        item.dataPrincipal?.toLowerCase().includes(query) ||
        item.reason?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // DF Status filter
    if (filters.dfStatus.length > 0) {
      const itemStatus = item.dfStatus?.toUpperCase() || "";
      const matchesStatus = filters.dfStatus.some(
        (status) => status.toUpperCase() === itemStatus
      );
      if (!matchesStatus) return false;
    }

    // Overall Status filter
    if (filters.overallStatus.length > 0) {
      const itemStatus = item.overallStatus?.toLowerCase() || "";
      const matchesStatus = filters.overallStatus.some(
        (status) => status.toLowerCase() === itemStatus
      );
      if (!matchesStatus) return false;
    }

    // Reason filter
    if (filters.reason.length > 0) {
      const itemReason = item.reason?.toUpperCase() || "";
      const matchesReason = filters.reason.some(
        (reason) => reason.toUpperCase() === itemReason
      );
      if (!matchesReason) return false;
    }

    // Date range filter – compare in IST so the user-selected dates match
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    if (hasDateFilter) {
      const rawTs = item.eventTimestamp;
      if (rawTs == null || rawTs === '') return false;

      // Robustly parse the event timestamp (handles epoch s/ms, ISO strings
      // with nanosecond precision, and strings without timezone suffix)
      let eventDate;
      if (typeof rawTs === 'number') {
        const ms = rawTs < 1e12 ? rawTs * 1000 : rawTs;
        eventDate = new Date(ms);
      } else if (typeof rawTs === 'string') {
        const trimmed = String(rawTs).trim();
        if (/^\d{10,13}$/.test(trimmed)) {
          const n = Number(trimmed);
          eventDate = new Date(n < 1e12 ? n * 1000 : n);
        } else {
          // Truncate fractional seconds to 3 digits so Date() can parse it
          const normalized = trimmed.replace(/(\.\d{3})\d+/, '$1');
          const hasTz = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
          eventDate = new Date(hasTz ? normalized : normalized + 'Z');
        }
      } else {
        return false;
      }

      if (isNaN(eventDate.getTime())) return false;

      // Convert to IST date string (YYYY-MM-DD) so we compare in the same
      // timezone the user sees; 'en-CA' locale produces YYYY-MM-DD format.
      const istDateStr = eventDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

      if (filters.dateFrom && istDateStr < filters.dateFrom) return false;
      if (filters.dateTo && istDateStr > filters.dateTo) return false;
    }

    return true;
  });

  // Stats are currently using dummy data matching the screenshot
  // TODO: Update with actual API data when available

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    return <Icon ic={<IcSort />} size="small" color="black" />;
  };

  const sortedRequests = useMemo(() => {
    if (!sortColumn) return filteredRequests;

    return [...filteredRequests].sort((a, b) => {
      // Handle processor fraction sorting (avoid division by zero)
      if (sortColumn === 'processors') {
        const aTotal = a.processorsTotal || 0;
        const bTotal = b.processorsTotal || 0;
        const aRatio = aTotal > 0 ? a.processorsCompleted / aTotal : 0;
        const bRatio = bTotal > 0 ? b.processorsCompleted / bTotal : 0;
        if (aRatio < bRatio) return sortDirection === 'asc' ? -1 : 1;
        if (aRatio > bRatio) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }

      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      // Normalize for string comparison
      const aStr = aValue != null ? String(aValue).toLowerCase() : '';
      const bStr = bValue != null ? String(bValue).toLowerCase() : '';

      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRequests, sortColumn, sortDirection]);

  // Handle CSV download – use explicit headers and row mapping so columns never shift
  const CSV_HEADERS = [
    'Consent ID',
    'Data Principal',
    'Reason',
    'DF Status',
    'Processors',
    'Overall Deletion Status',
    'Event Timestamp',
  ];

  const handleDownloadCSV = () => {
    if (!sortedRequests || sortedRequests.length === 0) {
      alert("No data available to download");
      return;
    }

    const csvData = sortedRequests.map((request) => {
      // Use "completed / total" format with spaces around the slash so Excel
      // does NOT auto-interpret values like 0/1 as dates (01-Jan).
      const completed = safeNumber(request.processorsCompleted);
      const total = safeNumber(request.processorsTotal);
      const processorsStr = `${completed} / ${total}`;
      return {
        [CSV_HEADERS[0]]: request.consentId ?? 'N/A',
        [CSV_HEADERS[1]]: request.dataPrincipal ?? 'N/A',
        [CSV_HEADERS[2]]: request.reason ?? 'N/A',
        [CSV_HEADERS[3]]: request.dfStatus ?? 'N/A',
        [CSV_HEADERS[4]]: processorsStr,
        [CSV_HEADERS[5]]: request.overallStatus ?? 'N/A',
        [CSV_HEADERS[6]]: formatToIST(request.eventTimestamp),
      };
    });

    exportToCSV(csvData, 'data_deletion_purge_requests', CSV_HEADERS);
  };

  // Handle filter changes
  const handleDFStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      dfStatus: prev.dfStatus.includes(status)
        ? prev.dfStatus.filter((s) => s !== status)
        : [...prev.dfStatus, status],
    }));
  };

  const handleOverallStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      overallStatus: prev.overallStatus.includes(status)
        ? prev.overallStatus.filter((s) => s !== status)
        : [...prev.overallStatus, status],
    }));
  };

  const handleReasonFilterChange = (reason) => {
    setFilters((prev) => ({
      ...prev,
      reason: prev.reason.includes(reason)
        ? prev.reason.filter((r) => r !== reason)
        : [...prev.reason, reason],
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      dfStatus: [],
      overallStatus: [],
      reason: [],
      dateFrom: "",
      dateTo: "",
    });
  };

  const handleApplyFilters = () => {
    setFilterDrawerOpen(false);
  };

  // Get active filter count
  const activeFilterCount =
    filters.dfStatus.length +
    filters.overallStatus.length +
    filters.reason.length +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0);

  // Helper function to get DF status badge with custom colors matching screenshot
  const getDFStatusBadge = (status) => {
    const badgeStyle = {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      textAlign: 'center',
    };

    switch (status) {
      case "Deleted":
        return (
          <span
            style={{
              ...badgeStyle,
              backgroundColor: '#D1FAE5', // Light green background
              color: '#065F46', // Dark green text
            }}
          >
            {status}
          </span>
        );
      case "Pending":
        return (
          <span
            style={{
              ...badgeStyle,
              backgroundColor: '#FED7AA', // Light orange background
              color: '#C2410C', // Dark orange text
            }}
          >
            {status}
          </span>
        );
      case "Deferred":
        return (
          <span
            style={{
              ...badgeStyle,
              backgroundColor: '#F3F4F6', // Light grey background
              color: '#6B7280', // Dark grey text
            }}
          >
            {status}
          </span>
        );
      default:
        return <Text appearance="body-xs">{status}</Text>;
    }
  };

  // Helper function to get overall status (plain text, no coloring)
  const getOverallStatusBadge = (status) => {
    return <Text appearance="body-xs" color="black">{status}</Text>;
  };

  return (
    <div className="configurePage" style={{ padding: "32px", minHeight: "calc(100vh - 80px)", boxSizing: "border-box", width: "100%", maxWidth: "100%", overflowX: "hidden", backgroundColor: '#F8FAFC', position: 'relative' }}>
      {/* Decorative background blob */}
      <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '40%', height: '50%', background: '#E0F2FE', borderRadius: '50% 40% 60% 40%', transform: 'rotate(-15deg)', filter: 'blur(80px)', zIndex: 0, opacity: 0.6 }}></div>

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '100%', margin: '0 auto' }}>
        
        {/* Header Section */}
        <div className="template-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div className="template-title-section">
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text appearance="heading-s" color="primary-grey-100">Data deletion and purge dashboard</Text>
                <div className="custom-header-tooltip-container" style={{ display: 'flex' }}>
                  <div className="custom-header-tooltip-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </div>
                  <div className="custom-header-tooltip-text" style={{ width: '280px', fontWeight: 'normal' }}>
                    Monitor data deletion requests and purge history.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Container Card */}
        <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 180px)' }}>

      {loading ? (
        <div className="configure-template-page">
          <div className="template-img">
            <Image src={dashboard}></Image>
          </div>
          <Text color="primary-grey-80" style={{ marginTop: "0.5rem" }}>
            Loading deletion requests...
          </Text>
        </div>
      ) : error ? (
        <div className="configure-template-page">
          <div className="template-img">
            <Image src={dashboard}></Image>
          </div>
          <Text color="primary-grey-80" style={{ marginTop: "0.5rem" }}>
            Error loading deletion requests: {error}
          </Text>
        </div>
      ) : (
        <>
          {/* Stats Section */}
          <div style={{ marginTop: '24px', marginBottom: '16px' }}>
            <Text appearance="body-m-bold" color="primary-grey-80">Overview metrics</Text>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px',
            marginBottom: '0',
            width: '94%'
          }}>
            {/* Card 1: Deletion requests */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <IcTrash height={24} width={24} color="#DC2626" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text appearance="heading-xs" color="primary-grey-100" style={{ fontWeight: '600' }}>
                  {String(stats.deletionRequests ?? 0)}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  Deletion requests
                </Text>
              </div>
            </div>

            {/* Card 2: Completed */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#D1FAE5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <IcSuccessColored height={24} width={24} color="#25AB21" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text appearance="heading-xs" color="primary-grey-100" style={{ fontWeight: '600' }}>
                  {String(stats.completed ?? 0)}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  Completed
                </Text>
              </div>
            </div>

            {/* Card 3: In progress */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#DBEAFE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <IcRefresh height={24} width={24} color="#2563EB" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text appearance="heading-xs" color="primary-grey-100" style={{ fontWeight: '600' }}>
                  {String(stats.inProgress ?? 0)}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  In progress
                </Text>
              </div>
            </div>

            {/* Card 4: User notified */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#FCE7F3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexShrink: 0
              }}>
                <IcProfile height={24} width={24} color="#EC4899" />
                <div style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#D1FAE5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #FFFFFF'
                }}>
                  <IcSuccess height={12} width={12} color="#25AB21" />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text appearance="heading-xs" color="primary-grey-100" style={{ fontWeight: '600' }}>
                  {String(stats.userNotified ?? 0)}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  User notified
                </Text>
              </div>
            </div>

            {/* Card 5: Notification pending */}
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#F3E8FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexShrink: 0
              }}>
                <IcNotification height={24} width={24} color="#9333EA" />
                <div style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#FED7AA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #FFFFFF'
                }}>
                  <IcWarningColored height={12} width={12} color="#C2410C" />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text appearance="heading-xs" color="primary-grey-100" style={{ fontWeight: '600' }}>
                  {String(stats.notificationPending ?? 0)}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  Notification pending
                </Text>
              </div>
            </div>
          </div>

          {/* Consent deletion requests section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '32px',
            marginBottom: '16px'
          }}>
            <Text appearance="body-m-bold" color="primary-grey-80">
              Consent deletion requests
            </Text>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div className="template-search-container" style={{ margin: 0 }}>
                <input
                  type="text"
                  placeholder="Search system name or description"
                  className="template-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg className="template-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <button
                className={`template-icon-button ${filterDrawerOpen ? "active" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`}
                onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                title="Filter"
                style={{ margin: 0 }}
              >
                <IcFilter height={20} width={20} />
                {activeFilterCount > 0 && (
                  <span className="filter-badge">{activeFilterCount}</span>
                )}
              </button>
              <button className="template-icon-button" onClick={handleDownloadCSV} title="Download CSV">
                <IcDownload height={20} width={20} />
              </button>
            </div>
          </div>

          {/* Filter Drawer */}
          {filterDrawerOpen && (
            <>
              <div
                className="filter-drawer-overlay"
                onClick={() => setFilterDrawerOpen(false)}
              />
              <div className="filter-drawer">
                <div className="filter-drawer-header">
                  <div className="filter-drawer-title">
                    <IcFilter height={24} width={24} />
                    <Text appearance="heading-xs" color="primary-grey-100">
                      Filters
                    </Text>
                  </div>
                  <button
                    className="filter-drawer-close"
                    onClick={() => setFilterDrawerOpen(false)}
                  >
                    <IcClose height={24} width={24} />
                  </button>
                </div>

                <div className="filter-drawer-content">
                  {/* DF Status Filter */}
                  <div className="filter-section">
                    <Text appearance="body-s-bold" color="primary-grey-100">
                      DF Status
                    </Text>
                    <div className="filter-chips">
                      {["Deleted", "Pending", "Deferred"].map((status) => (
                        <button
                          key={status}
                          className={`filter-chip ${filters.dfStatus.includes(status) ? "active" : ""}`}
                          onClick={() => handleDFStatusFilterChange(status)}
                        >
                          {status}
                          {filters.dfStatus.includes(status) && (
                            <IcClose height={16} width={16} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Overall Status Filter */}
                  <div className="filter-section">
                    <Text appearance="body-s-bold" color="primary-grey-100">
                      Overall Deletion Status
                    </Text>
                    <div className="filter-chips">
                      {["All deleted", "Partial deleted", "Deferred"].map((status) => (
                        <button
                          key={status}
                          className={`filter-chip ${filters.overallStatus.includes(status) ? "active" : ""}`}
                          onClick={() => handleOverallStatusFilterChange(status)}
                        >
                          {status}
                          {filters.overallStatus.includes(status) && (
                            <IcClose height={16} width={16} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reason Filter */}
                  <div className="filter-section">
                    <Text appearance="body-s-bold" color="primary-grey-100">
                      Reason
                    </Text>
                    <div className="filter-chips">
                      {["Withdraw", "Expiry"].map((reason) => (
                        <button
                          key={reason}
                          className={`filter-chip ${filters.reason.includes(reason) ? "active" : ""}`}
                          onClick={() => handleReasonFilterChange(reason)}
                        >
                          {reason}
                          {filters.reason.includes(reason) && (
                            <IcClose height={16} width={16} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Created Date Range */}
                  <div className="filter-section">
                    <Text appearance="body-s-bold" color="primary-grey-100">
                      Created date range
                    </Text>
                    <div className="filter-date-range">
                      <div className="filter-date-input">
                        <label>From</label>
                        <input
                          type="date"
                          value={filters.dateFrom}
                          max={filters.dateTo || new Date().toISOString().split('T')[0]}
                          onChange={(e) =>
                            setFilters({ ...filters, dateFrom: e.target.value })
                          }
                        />
                      </div>
                      <div className="filter-date-input">
                        <label>To</label>
                        <input
                          type="date"
                          value={filters.dateTo}
                          min={filters.dateFrom || ''}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={(e) =>
                            setFilters({ ...filters, dateTo: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="filter-drawer-footer">
                  <ActionButton
                    kind="secondary"
                    size="medium"
                    label="Clear all"
                    onClick={handleClearFilters}
                  />
                  <ActionButton
                    kind="primary"
                    size="medium"
                    label="Apply"
                    onClick={handleApplyFilters}
                  />
                </div>
              </div>
            </>
          )}

          {/* Table Section */}
          <div className="template-table-wrapper">
            <table className="template-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('consentId')} style={{ cursor: 'pointer' }}>
                    <div className="header-with-icon">
                      <Text appearance="body-xs-bold" color="primary-grey-80">
                        Consent ID
                      </Text>
                      {renderSortIcon('consentId')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('dataPrincipal')} style={{ cursor: 'pointer' }}>
                    <div className="header-with-icon">
                      <Text appearance="body-xs-bold" color="primary-grey-80">
                        Data principal
                      </Text>
                      {renderSortIcon('dataPrincipal')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('reason')} style={{ cursor: 'pointer' }}>
                    <div className="header-with-icon">
                      <Text appearance="body-xs-bold" color="primary-grey-80">
                        Reason
                      </Text>
                      {renderSortIcon('reason')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('dfStatus')} style={{ cursor: 'pointer' }}>
                    <div className="header-with-icon">
                      <Text appearance="body-xs-bold" color="primary-grey-80">
                        DF status
                      </Text>
                      {renderSortIcon('dfStatus')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('processors')} style={{ cursor: 'pointer' }}>
                    <div className="header-with-icon">
                      <Text appearance="body-xs-bold" color="primary-grey-80">
                        Processors
                      </Text>
                      {renderSortIcon('processors')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('overallStatus')} style={{ cursor: 'pointer' }}>
                    <div className="header-with-icon">
                      <Text appearance="body-xs-bold" color="primary-grey-80">
                        Overall deletion status
                      </Text>
                      {renderSortIcon('overallStatus')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRequests.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                      <Text appearance="body-m" color="primary-grey-80">
                        {searchQuery ? `No results found for "${searchQuery}"` : "No deletion requests found."}
                      </Text>
                    </td>
                  </tr>
                ) : (
                  sortedRequests.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div
                          style={{
                            cursor: 'pointer',
                            display: 'inline-block'
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/consent-deletion-detail/${item.eventId}`);
                          }}
                        >
                          <Text
                            appearance='body-xs'
                            color="primary"
                            style={{ textDecoration: 'underline' }}
                          >
                            {item.consentId}
                          </Text>
                        </div>
                      </td>
                      <td>
                        <Text appearance="body-xs" color="black">
                          {item.dataPrincipal}
                        </Text>
                      </td>
                      <td>
                        <Text appearance="body-xs" color="black">
                          {item.reason}
                        </Text>
                      </td>
                      <td>
                        {getDFStatusBadge(item.dfStatus)}
                      </td>
                      <td>
                        <Text appearance="body-xs" color="black">
                          {item.processorsDisplay || `${safeNumber(item.processorsCompleted)}/${safeNumber(item.processorsTotal)}`}
                        </Text>
                      </td>
                      <td>
                        {getOverallStatusBadge(item.overallStatus)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
        </div>
      </div>
    </div>
  );
};

export default DataDeletionPurgeDashboard;

